/**
 * tenant-promo app の route 定数（LE-00 / promo feature）。
 */
export const ROUTES = {
  home: '/',
  /** 顧客一覧（= index）。興味タグ / 車検期限 / WashPass / 最終来店でセグメント絞り込み。 */
  customers: '/',
  /** 分析サマリー（M-161: 顧客管理の上段から独立 / 顧客在庫の 9 セクション）。 */
  analytics: '/analytics',
  /** 顧客詳細（プロフィール + 興味タグ + 行動ログタイムライン + 来店履歴）。 */
  customerDetail: (uid: string) => `/customers/${encodeURIComponent(uid)}`,
  /** セグメント配信作成（条件 → 人数プレビュー → 本文 → 送信）。 */
  segmentCompose: '/segments/new',
  /** セグメント配信作成（業態プリセット付き / 販促プランからの導線 / M-56）。 */
  segmentComposeForInterest: (interestKey: string) =>
    `/segments/new?interestKey=${encodeURIComponent(interestKey)}`,
  /** セグメント配信作成（業態 + アンケートプリセット付き / 販促プランのアンケート導線 / M-78）。 */
  segmentComposeForSurvey: (interestKey: string, surveyId: string) =>
    `/segments/new?interestKey=${encodeURIComponent(interestKey)}&surveyId=${encodeURIComponent(surveyId)}`,
  /** HOT 案件一覧（担当者の商談管理 / M-76）。 */
  hotLeads: '/hot-leads',
  /**
   * 配信ツリー（M-143）。**何をきっかけに・どの配信が・どの順で届くか**を一望する。
   * M-143 以前は商品別販促プラン一覧だった（そちらは `kpi` へ移動）。
   */
  plans: '/plans',
  /** 配信ツリーで特定のノードを選択した状態（`?node=` / 編集画面からの戻り先にも使う）。 */
  plansNode: (nodeId: string) => `/plans?node=${encodeURIComponent(nodeId)}`,
  /** KPI 画面（M-153: 商材ごとのゴール × 実測。旧「販促プラン」一覧は `kpiPlans` へ）。 */
  kpi: '/kpi',
  /** 旧 URL（M-164 初版）。`eventStatus` へリダイレクト。 */
  lottery: '/lottery',
  /** M-164 上部タブ「イベント」: ルーレット設定（期間 / 一時停止 / 1 人 1 日 / 重み・本数・日別上限）。 */
  eventSettings: '/event/settings',
  /** 当選クーポン（全員分 + 景品ごとの複製・配布済・使用済 / 編集は既存のクーポン編集へ）。 */
  eventCoupons: '/event/coupons',
  /** 抽選状況（閲覧のみ: 本日・累計の抽選数、景品別の当選・残数、多回数の利用者、エラー件数）。 */
  eventStatus: '/event/status',
  /** 販促プラン一覧（目標値・施策メモ = 旧 /kpi の中身 / M-153 で移動）。 */
  kpiPlans: '/kpi/plans',
  /** KPI プラン新規作成（商品特性を選んで組み立てる / M-90）。 */
  kpiPlanNew: '/kpi/new',
  /** KPI プラン新規作成（商材プリセット付き / M-90）。 */
  kpiPlanNewForInterest: (interestKey: string) =>
    `/kpi/new?interestKey=${encodeURIComponent(interestKey)}`,
  /**
   * KPI プラン編集。M-90 以前に作られた doc の id は `${tenantId}__${interestKey}` 形式で
   * `__` を含むが、`_` は RFC 3986 の unreserved なので `encodeURIComponent` は素通しする。
   */
  kpiPlanEdit: (planId: string) => `/kpi/${encodeURIComponent(planId)}`,
  /**
   * シナリオ配信の新規作成（M-91）。シナリオは保存時に planId が必須なので `?planId=` で渡す
   * （M-143 で `/plans/:planId/scenarios/new` から平坦化）。
   */
  scenarioNew: (planId: string) => `/scenarios/new?planId=${encodeURIComponent(planId)}`,
  /** シナリオ配信の編集（M-143 で scenarioId だけで開けるようにした / planId は doc から引く）。 */
  scenarioEdit: (scenarioId: string) => `/scenarios/${encodeURIComponent(scenarioId)}`,
  /** アンケート一覧（商材横断 / M-94 でプラン配下から独立メニュー化）。 */
  surveys: '/surveys',
  /** アンケート新規作成（M-94）。 */
  surveyNew: '/surveys/new',
  /** アンケート新規作成（商材プリセット付き / 販促プランからの導線 / M-94）。 */
  surveyNewForInterest: (interestKey: string) =>
    `/surveys/new?interestKey=${encodeURIComponent(interestKey)}`,
  /** アンケート編集（左に設定・右に顧客プレビュー / M-94）。 */
  surveyEdit: (surveyId: string) => `/surveys/${encodeURIComponent(surveyId)}`,
  /** 配信履歴。 */
  campaigns: '/campaigns',
  /** 予約配信の一覧（予約済み / 取消）。 */
  scheduledPushes: '/segments/scheduled',
  /** クーポン一覧（LIFF 独自クーポン / 取得＝反応計測）。 */
  coupons: '/coupons',
  /** クーポン新規作成。 */
  couponNew: '/coupons/new',
  /** クーポン複製（内容を引き継いだ新規作成 / `?copyFrom=<id>`）。 */
  couponCopy: (couponId: string) =>
    `/coupons/new?copyFrom=${encodeURIComponent(couponId)}`,
  /** クーポン編集。 */
  couponEdit: (couponId: string) => `/coupons/${encodeURIComponent(couponId)}/edit`,
  /** クーポン取得ログ。 */
  couponUsages: (couponId: string) => `/coupons/${encodeURIComponent(couponId)}/usages`,
  /** 予約特典クーポン一覧（M-51 / 予約完了で自動配信 / 来店日限定）。 */
  rewardCoupons: '/reward-coupons',
  /** 予約特典クーポン新規作成。 */
  rewardCouponNew: '/reward-coupons/new',
  /** 予約特典クーポン複製（`?copyFrom=<id>`）。 */
  rewardCouponCopy: (couponId: string) =>
    `/reward-coupons/new?copyFrom=${encodeURIComponent(couponId)}`,
  /** 予約特典クーポン編集。 */
  rewardCouponEdit: (couponId: string) =>
    `/reward-coupons/${encodeURIComponent(couponId)}/edit`,
  /** 予約特典クーポン取得ログ。 */
  rewardCouponUsages: (couponId: string) =>
    `/reward-coupons/${encodeURIComponent(couponId)}/usages`,
  /** 初回診断アンケート（可視化 / 設定 / 配信実行 / M-124）。 */
  diagnosis: '/diagnosis',
  /** 確度エンジンの仕組み（読み物 / 商材ごとの実設定値つき）。 */
  engineGuide: '/engine-guide',
  signIn: '/signin',
} as const;

/** route パターン定義（react-router の path に渡す静的文字列）。 */
export const ROUTE_PATHS = {
  customerDetail: '/customers/:uid',
  analytics: '/analytics',
  segmentCompose: '/segments/new',
  hotLeads: '/hot-leads',
  plans: '/plans',
  kpi: '/kpi',
  lottery: '/lottery',
  eventSettings: '/event/settings',
  eventCoupons: '/event/coupons',
  eventStatus: '/event/status',
  // `/kpi/plans` `/kpi/new` は静的セグメントなので `:planId` より先に宣言する。
  kpiPlans: '/kpi/plans',
  kpiPlanNew: '/kpi/new',
  kpiPlanEdit: '/kpi/:planId',
  // `/scenarios/new` は静的セグメントなので `:scenarioId` より先に宣言する。
  scenarioNew: '/scenarios/new',
  scenarioEdit: '/scenarios/:scenarioId',
  // M-143 以前の URL（ブックマーク / 配信済みリンク対策 = LegacyRedirect が新 URL へ送る）。
  legacyPlanNew: '/plans/new',
  legacyPlanEdit: '/plans/:planId',
  legacyScenarioNew: '/plans/:planId/scenarios/new',
  legacyScenarioEdit: '/plans/:planId/scenarios/:scenarioId',
  scheduledPushes: '/segments/scheduled',
  surveys: '/surveys',
  // `/surveys/new` は静的セグメントなので `:surveyId` より先に宣言する
  // （couponNew → couponEdit / planNew → planEdit と同じ並び）。
  surveyNew: '/surveys/new',
  surveyEdit: '/surveys/:surveyId',
  campaigns: '/campaigns',
  coupons: '/coupons',
  couponNew: '/coupons/new',
  couponEdit: '/coupons/:couponId/edit',
  couponUsages: '/coupons/:couponId/usages',
  rewardCoupons: '/reward-coupons',
  rewardCouponNew: '/reward-coupons/new',
  rewardCouponEdit: '/reward-coupons/:couponId/edit',
  rewardCouponUsages: '/reward-coupons/:couponId/usages',
  diagnosis: '/diagnosis',
  engineGuide: '/engine-guide',
} as const;
